/**
 * Elevation shots: their numbers (as tests', the lowest free from where the map's numbering starts, so
 * a shot deleted frees its number), what their tags say ("#1 Elev. 2795.65" and, measured against a
 * grade the inspector typed, "9.89 ft below FG") and the checks that catch a slip of the finger.
 * An elevation is always shown exactly as it was typed: never rounded, never filled in.
 */
import type { Elevation, ElevMeta, ElevRef, Item } from '../../shared/doc.ts';
import { ELEV_PREFIX_DEFAULT } from '../../shared/doc.ts';

/** The numbers the elevation shots use. */
function used(items: readonly Item[]): Set<number> {
  const out = new Set<number>();
  for (const it of items) if (it.t === 'elev') out.add(it.n);
  return out;
}
/** The number the next shot takes: the lowest not in use from where the map's numbering starts (1, unless it was set) — a shot deleted frees its number. */
export function nextElevNumber(items: readonly Item[], start?: number): number {
  const u = used(items);
  let n = Math.max(1, start ?? 1);
  while (u.has(n)) n++;
  return n;
}
/** What a map calls an elevation on its tags ("Elev." unless it says otherwise). */
export function mapPrefix(meta: ElevMeta | undefined): string {
  return meta?.prefix ?? ELEV_PREFIX_DEFAULT;
}
/** What a shot's tag calls its elevation: its own word, else the map's. */
export function prefixOf(e: Pick<Elevation, 'prefix'>, meta: ElevMeta | undefined): string {
  return e.prefix ?? mapPrefix(meta);
}
/** The map's unit, as written after a depth ("ft", "m"). */
export function unitOf(meta: ElevMeta | undefined): string {
  return meta?.unit === 'm' ? 'm' : 'ft';
}
/**
 * An elevation as the tag shows it: as typed, with a point left hanging taken off ("2795." reads
 * "2795"), a bare point given its zero (".5" reads "0.5") and a true minus sign. Never rounded.
 */
export function shownValue(value: string): string {
  let v = value.trim();
  if (v.endsWith('.')) v = v.slice(0, -1);
  if (v.startsWith('.')) v = '0' + v;
  else if (v.startsWith('-.')) v = '-0' + v.slice(1);
  if (v === '-' || v === '') return '';
  return v.startsWith('-') ? `−${v.slice(1)}` : v;
}
/** Is a value an elevation (a number, however written)? */
export function isElevation(value: string): boolean {
  return /^-?(\d+\.?\d*|\.\d+)$/.test(value.trim());
}
/** How many places after the point a value was typed to. */
function places(v: string): number {
  const i = v.indexOf('.');
  return i < 0 ? 0 : v.length - i - 1;
}
/** A value as a whole number of 10^-d units, exactly (no floating point on the way). */
function scaled(v: string, d: number): number {
  const neg = v.trim().startsWith('-');
  const [a = '', b = ''] = v.trim().replace('-', '').split('.');
  const digits = (a || '0') + b.padEnd(d, '0').slice(0, d);
  const n = Number.parseInt(digits, 10);
  return neg ? -n : n;
}
/**
 * How far a shot is below (or above) the grade it is measured against, in words, exactly to the
 * places the two were typed to: "9.89 ft below FG", "0.25 ft above EG", "at FG". Null when either is
 * no number.
 */
export function depthText(value: string, ref: ElevRef | undefined, meta: ElevMeta | undefined): string | null {
  if (!ref || !isElevation(value) || !isElevation(ref.value)) return null;
  const d = Math.max(places(value.trim()), places(ref.value.trim()));
  const diff = scaled(ref.value, d) - scaled(value, d);
  const name = ref.name.trim() || shownValue(ref.value);
  if (diff === 0) return `at ${name}`;
  const abs = String(Math.abs(diff)).padStart(d + 1, '0');
  const text = d ? `${abs.slice(0, abs.length - d)}.${abs.slice(abs.length - d)}` : abs;
  return `${text} ${unitOf(meta)} ${diff > 0 ? 'below' : 'above'} ${name}`;
}
/** The words of a shot's tag after its number: "Elev. 2795.65", and its note. `draft`: the value being typed, as shown. */
export function elevWords(e: Elevation, meta: ElevMeta | undefined, draft?: string): string {
  const value = draft ?? (shownValue(e.value) || '—');
  return [prefixOf(e, meta), value, e.note ?? ''].filter(Boolean).join(' ');
}
/** A shot's whole tag as it reads: "#1 Elev. 2795.65", and its depth after it. */
export function elevText(e: Elevation, meta: ElevMeta | undefined): string {
  const depth = depthText(e.value, e.ref, meta);
  return `#${e.n} ${elevWords(e, meta)}${depth ? ` (${depth})` : ''}`;
}
/**
 * A shot that looks like a slip of the finger, in words (null: it looks right): an elevation far from
 * the other shots on the map (50 ft and more from their middle — a digit dropped or the point moved),
 * or typed to more places than a level or a rover reads. Only ever said, never put right.
 */
export function elevWarning(e: Elevation, items: readonly Item[]): string | null {
  if (!isElevation(e.value)) return null;
  const v = Number(e.value);
  const others = items.flatMap((x) => (x.t === 'elev' && x.id !== e.id && isElevation(x.value) ? [Number(x.value)] : [])).sort((a, b) => a - b);
  if (others.length) {
    const mid = others[Math.floor(others.length / 2)]!;
    if (Math.abs(v - mid) >= 50) return `#${e.n} reads ${shownValue(e.value)} — the other shots are near ${Math.round(mid)}. Check it.`;
  }
  if (places(e.value.trim()) > 3) return `#${e.n} has ${places(e.value.trim())} places after the point — check it.`;
  return null;
}
/** Every shot on a map as lines of text, for a report: "#1  Elev. 2795.65 ft (9.89 ft below FG)". */
export function elevationsText(items: readonly Item[], meta: ElevMeta | undefined): string {
  const shots = items.filter((x): x is Elevation => x.t === 'elev').sort((a, b) => a.n - b.n);
  const unit = unitOf(meta);
  const head = meta?.what ? `Elevations, ${meta.what} (${unit})` : `Elevations (${unit})`;
  const lines = shots.map((e) => {
    const depth = depthText(e.value, e.ref, meta);
    return `#${e.n}  ${prefixOf(e, meta) ? `${prefixOf(e, meta)} ` : ''}${shownValue(e.value) || '—'}${e.note ? ` ${e.note.replace(/\s*\n\s*/g, ' ')}` : ''}${depth ? ` (${depth})` : ''}`;
  });
  return [head, ...lines, ...(meta?.source ? [`Taken by ${meta.source}.`] : [])].join('\n');
}
